import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { apiService } from '../../services/api';
import { phoneMask, removePhoneMask } from '../../utils/masks';
import authService from '../../services/authService';
import Toast from '../Toast/Toast';

interface FormPacienteProps {
  pacienteId?: string;
  onVoltar: () => void;
  onSalvar: () => void;
}

interface PacienteForm {
  nome: string;
  email: string;
  telefone: string;
}

const FormPaciente: React.FC<FormPacienteProps> = ({ pacienteId, onVoltar, onSalvar }) => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isNutricionista, setIsNutricionista] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' as 'success' | 'warning' | 'danger' | 'info' });
  const [formData, setFormData] = useState<PacienteForm>({
    nome: '',
    email: '',
    telefone: ''
  });

  useEffect(() => {
    const checkUserType = async () => {
      try {
        const userProfile = await authService.getProfile();
        setIsNutricionista(userProfile.tipo === 'nutricionista');
      } catch (error) {
        console.error('Erro ao verificar perfil:', error);
      }
    };
    checkUserType();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (pacienteId) {
      carregarPaciente();
    }
  }, [pacienteId]);

  const carregarPaciente = async () => {
    try {
      setLoading(true);
      const data = await apiService.getPaciente(pacienteId!);
      
      setFormData({
        nome: data.nome.toUpperCase(),
        email: data.email,
        telefone: phoneMask(data.telefone || '')
      });
    } catch (error) {
      console.error('Erro ao carregar paciente:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'telefone') {
      const masked = phoneMask(value);
      setFormData(prev => ({
        ...prev,
        [name]: masked
      }));
    } else if (name === 'nome') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const cleanPhone = removePhoneMask(formData.telefone);
      if (cleanPhone.length > 0 && cleanPhone.length < 10) {
        setToast({ show: true, message: 'Telefone deve ter pelo menos 10 dígitos', type: 'warning' });
        return;
      }
      
      const payload: any = {
        ...formData,
        nome: formData.nome.toUpperCase(),
        telefone: cleanPhone
      };
      
      // Adicionar senha automática apenas se for nutricionista cadastrando novo paciente
      if (!pacienteId && isNutricionista) {
        const primeiroNome = formData.nome.split(' ')[0].toLowerCase();
        payload.senha = `${primeiroNome}@2025`;
      }
      
      if (pacienteId) {
        await apiService.updatePaciente(pacienteId, payload);
      } else {
        await apiService.createPaciente(payload);
      }
      
      onSalvar();
    } catch (error: any) {
      console.error('Erro ao salvar paciente:', error);
      const message = error.response?.data?.message || 'Erro ao salvar paciente. Tente novamente.';
      setToast({ show: true, message, type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast 
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
      <div className="container-fluid p-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <button 
          className="btn btn-outline-secondary"
          onClick={onVoltar}
          disabled={loading}
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-success mb-0">
          {pacienteId ? 'Editar Paciente' : 'Novo Paciente'}
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0">Dados do Paciente</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="paciente-nome" className="form-label">Nome Completo *</label>
                <input
                  id="paciente-nome"
                  type="text"
                  className="form-control"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="paciente-email" className="form-label">Email *</label>
                <input
                  id="paciente-email"
                  type="email"
                  className="form-control"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="paciente-telefone" className="form-label">Telefone</label>
                <input
                  id="paciente-telefone"
                  type="tel"
                  className="form-control"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button 
            type="button" 
            className="btn btn-outline-secondary border"
            onClick={onVoltar}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="btn btn-success d-flex align-items-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <output className="spinner-border spinner-border-sm">
                  <span className="visually-hidden">Salvando...</span>
                </output>
                Salvando...
              </>
            ) : (
              <>
              {!isMobile && <Save size={20} />}
              {pacienteId ? 'Atualizar Dados' : 'Salvar Paciente'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
    </>
  );
};

export default FormPaciente;